import React, { useState, useEffect } from 'react';

// Material UI
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';

// Material UI Icons
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import SaveIcon from '@mui/icons-material/Save';

// Componentes 

// Funciones para obtener datos
import ObtenerSocio from '../functions/ObtenerSocio';
import GuardarSocio from '../functions/GuardarSocio';
import DatePicker from './DatePicker';

// Tipos de datos
import SocioEditar from '../types/SocioEditar';

// Indicador de procesamiento
import CircularProgress from '@mui/material/CircularProgress';

interface EditarSocioProps {
    idSocio: number;
    guardarCambios: (socio: SocioEditar) => void;
    cancelarCambios: () => void;
}

export const EditarSocio: React.FC<EditarSocioProps> = ({ idSocio, guardarCambios, cancelarCambios }) => {
  
    const [isLoading, setIsLoading] = useState(true);
    const [socio, setSocio] = useState<SocioEditar | null>(null);
    const [errorSocio, setErrorSocio] = useState<string | null>(null);

    const [localDocumento, setLocalDocumento] = useState('');
    const [localApellido, setLocalApellido] = useState('');
    const [localNombres, setLocalNombres] = useState('');
    const [localFchnac, setLocalFchnac] = useState('');
    const [localFchNacValida, setLocalFchNacValida] = useState(false);
    const [localSexo, setLocalSexo] = useState('');
    const [localTiposocio, setLocalTiposocio] = useState('');
    const [localEmail, setLocalEmail] = useState('');
    const [localCelular, setLocalCelular] = useState('');
    const [localDoctitular, setLocalDoctitular] = useState('');
    const [localOrigen, setLocalOrigen] = useState('');
    const [localHabilitado, setLocalHabilitado] = useState('');
    const [localAgente, setLocalAgente] = useState('');
    const [localIdplan, setLocalIdplan] = useState(0);
    const [localDadoBaja, setLocalDadoBaja] = useState('');
    const [localPagoPendiente, setLocalPagoPendiente] = useState('');
    const [localReferencia, setLocalReferencia] = useState('');

    useEffect(() => {
        const fetchSocio = async () => {
            try {
                const data = await ObtenerSocio(idSocio);
                if (data.socio_ok === 'si') {
                    setSocio(data.socio[0]);

                    setLocalDocumento(data.socio[0].documento);
                    setLocalApellido(data.socio[0].apellido);
                    setLocalNombres(data.socio[0].nombres);
                    setLocalFchnac(data.socio[0].fchnac);
                    setLocalSexo(data.socio[0].sexo);
                    setLocalTiposocio(data.socio[0].tiposocio);
                    setLocalEmail(data.socio[0].email);
                    setLocalCelular(data.socio[0].celular);
                    setLocalDoctitular(data.socio[0].doctitular);
                    setLocalOrigen(data.socio[0].origen);
                    setLocalHabilitado(data.socio[0].habilitado);
                    setLocalAgente(data.socio[0].agente);
                    setLocalIdplan(data.socio[0].idplan);
                    setLocalDadoBaja(data.socio[0].dadobaja);
                    setLocalPagoPendiente(data.socio[0].pago_pendiente);
                    setLocalReferencia(data.socio[0].referencia);
                    setErrorSocio(null);

                } else {

                    setErrorSocio(data.mensaje);
                    setSocio(null);

                    setLocalDocumento('');
                    setLocalApellido('');
                    setLocalNombres('');
                    setLocalFchnac('');
                    setLocalSexo('');
                    setLocalTiposocio('');
                    setLocalEmail('');
                    setLocalCelular('');
                    setLocalDoctitular('');
                    setLocalOrigen('');
                    setLocalHabilitado('');
                    setLocalAgente('');
                    setLocalIdplan(0);
                    setLocalDadoBaja('');
                    setLocalPagoPendiente('');
                    setLocalReferencia('');
                }
                setIsLoading(false);
                
            } catch (err) {
                setErrorSocio('Error al obtener la información del socio.');
                setIsLoading(false);
                setSocio(null);
                setLocalDocumento('');
                setLocalApellido('');
                setLocalNombres('');
                setLocalFchnac('');
                setLocalSexo('');
                setLocalTiposocio('');
                setLocalEmail('');
                setLocalCelular('');
                setLocalDoctitular('');
                setLocalOrigen('');
                setLocalHabilitado('');
                setLocalAgente('');
                setLocalIdplan(0);       
                setLocalDadoBaja('');     
                setLocalPagoPendiente('');
                setLocalReferencia('');
            }
        };

        fetchSocio();

    }, []);

    // Handle fields changes
    const handleChangeLocalDocumento = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,8}$/;
        if (pattern.test(event.target.value)) {
            setLocalDocumento(event.target.value);
        }
    };

    const handleChangeLocalApellido = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalApellido(event.target.value.toUpperCase());
    };

    const handleChangeLocalNombres = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalNombres(event.target.value.toUpperCase());
    };
    
    const handleChangeLocalSexo = (event: SelectChangeEvent) => {
        setLocalSexo(event.target.value);
    };

    const handleChangeLocalEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLocalEmail(event.target.value);
    };

    const handleChangeLocalCelular = (event: React.ChangeEvent<HTMLInputElement>) => {
       const pattern = /^\d{0,10}$/;
        if (pattern.test(event.target.value)) {
            setLocalCelular(event.target.value);
        }
    };

    const handleChangeLocalHabitado = (event: SelectChangeEvent) => {
        setLocalHabilitado(event.target.value);
    };  

    const handleChangeLocalDadoBaja = (event: SelectChangeEvent) => {
        setLocalDadoBaja(event.target.value);
    };

    const handleChangeLocalPagoPendiente = (event: SelectChangeEvent) => {
        setLocalPagoPendiente(event.target.value);
    };

    const handleChangeLocalReferencia = (event: SelectChangeEvent) => {
        setLocalReferencia(event.target.value);
    };


    const handleFechaNacChange = (date: string) => {
        setLocalFchnac(date);
        
    };

    const checkFechaNacIsValid = (isValid: boolean) => {
        setLocalFchNacValida(isValid);
    };

    const handleChangeLocalDocumentoTitular = (event: React.ChangeEvent<HTMLInputElement>) => {
        const pattern = /^\d{0,8}$/;
        if (pattern.test(event.target.value)) {
            setLocalDoctitular(event.target.value);
        }
    };

    const guardarCambiosLocal = async () => {
        
        const socioEditado: SocioEditar = {
            id: idSocio,
            documento: localDocumento,
            apellido: localApellido,
            nombres: localNombres,
            fchnac: localFchnac,
            sexo: localSexo,
            tiposocio: localTiposocio,
            email: localEmail,
            celular: localCelular,
            doctitular: localDoctitular,
            origen: localOrigen,
            habilitado: localHabilitado,
            agente: localAgente,
            idplan: localIdplan,
            dadobaja: localDadoBaja,
            pago_pendiente: localPagoPendiente
        };

        const respuesta = await GuardarSocio(socioEditado);

        if (respuesta.socio_guardado === 'si') {
            guardarCambios(socioEditado);
        } else {
            alert('Error al guardar los cambios');
        }
    };

    return (
        <>
            {isLoading && (
                <div>
                    <p className="text-lg">Por favor espere</p>
                    <CircularProgress />
                </div>
            )}
            {(!isLoading && socio) && (
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <h1 className="font-metropolis text-xl font-bold mb-2">Editando Socio: {socio?.apellido.trim()}, {socio?.nombres.trim()}</h1>
                    <div className="border-t-2 border-b-2 border-vp-azul w-full flex flex-row pt-3 flex-wrap justify-between items-center space-y-2">
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="documento" label="Documento" variant="outlined" value={localDocumento}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    
                                }}
                                onChange={handleChangeLocalDocumento}
                            />
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[400px] flex-grow w-full">
                            <TextField id="apellido" label="Apellido" variant="outlined" value={localApellido}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalApellido}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[400px] flex-grow w-full">
                            <TextField id="nombres" label="Nombres" variant="outlined" value={localNombres}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalNombres}
                            />
                        </div>
                        {/*
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <TextField id="fchnac" label="Fec. Nacimiento" variant="outlined" value={localFchnac}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                
                            />
                        </div>*/}
                        <div className="flex flex-col justify-between items-center max-w-[250px] ">
                            <DatePicker etiqueta="Fec. Nac." initialDate={localFchnac} onDateChange={handleFechaNacChange} onDateValid={checkFechaNacIsValid}
                                ContainerSX={{
                                    width: undefined,
                                    padding: '4px',
                                    borderRadius: '5px',
                                    marginBottom: '8px',
                                }}
                                LabelSX={{
                                    fontSize: '12px',
                                }}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-sexo">Sexo</InputLabel>
                                <Select
                                    labelId="label-sexo"
                                    id="sexo"
                                    value={localSexo}
                                    label="Sexo"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalSexo}
                                >
                                    <MenuItem value={'M'}>Masc.</MenuItem>
                                    <MenuItem value={'F'}>Femen.</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Tipo de socio no editable */}
                        <div className="flex flex-col justify-between items-center max-w-[200px] flex-grow w-full">
                            <TextField id="tiposocio" label="Tipo de Socio" variant="outlined" value={localTiposocio} disabled
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                            />
                        </div>

                        {socio?.tiposocio.trim() === 'TITULAR' && (<>
                            <div className="flex flex-col justify-between items-center max-w-[300px] flex-grow w-full">
                                <TextField id="email" label="Email" variant="outlined" value={localEmail} 
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalEmail}
                                />
                            </div>
                            <div className="flex flex-col justify-between items-center max-w-[300px] flex-grow w-full">
                                <TextField id="celular" label="Celular" variant="outlined" value={localCelular} 
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalCelular}
                                />
                            </div></>
                        )}
                        <div className="flex flex-col justify-start items-center max-w-[120px] flex-grow w-full">
                            <TextField id="doctitular" label="Doc. Titular" variant="outlined" value={localDoctitular}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                                onChange={handleChangeLocalDocumentoTitular}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-origen">Origen</InputLabel>
                                <Select
                                    labelId="label-origen"
                                    id="origen"
                                    value={localOrigen}
                                    label="Origen"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                >
                                    <MenuItem value={'web'}>web</MenuItem>
                                    <MenuItem value={'back_office'}>back office</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-start items-center max-w-[300px] flex-grow w-full">
                            <TextField id="agente" label="Agente" variant="outlined" value={localAgente}
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                            />
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[300px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-plan">Plan</InputLabel>
                                <Select
                                    labelId="label-plan"
                                    id="plan"
                                    value={localIdplan}
                                    label="Origen"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    disabled
                                >
                                    <MenuItem value={'1'}>VidaPlus 100</MenuItem>
                                    <MenuItem value={'2'}>VidaPlus 200</MenuItem>
                                    <MenuItem value={'3'}>VidaPlus 300</MenuItem>
                                    <MenuItem value={'4'}>VidaPlus 400</MenuItem>
                                    <MenuItem value={'5'}>VidaPlus 500</MenuItem>
                                    <MenuItem value={'6'}>Vida Plus Senior 100</MenuItem>
                                    <MenuItem value={'7'}>Vida Plus Senior 200</MenuItem>
                                    <MenuItem value={'8'}>Vida Plus Senior 300</MenuItem>
                                    <MenuItem value={'14'}>Vida Plus Odonto 100</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-habilitado">Habilitado</InputLabel>
                                <Select
                                    labelId="label-habilitado"
                                    id="habilitado"
                                    value={localHabilitado}
                                    label="Habilitado"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalHabitado}
                                >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-baja">Baja</InputLabel>
                                <Select
                                    labelId="label-baja"
                                    id="baja"
                                    value={localDadoBaja}
                                    label="Baja"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalDadoBaja}
                                >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Pago Pendiente */}
                        <div className="flex flex-col justify-between items-center max-w-[120px] flex-grow w-full">
                            <FormControl fullWidth sx={{marginRight: '6px',}}>
                                <InputLabel id="label-pago-pendiente">Pago Pendiente</InputLabel>
                                <Select
                                    labelId="label-pago-pendiente"
                                    id="pago-pendiente"
                                    value={localPagoPendiente}
                                    label="Pago Pendiente"
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                        padding: '8px',
                                        },
                                        width: '100%',
                                        marginRight: '6px',
                                        marginBottom: '8px',
                                    }}
                                    onChange={handleChangeLocalPagoPendiente}
                                >
                                    <MenuItem value={'S'}>Si</MenuItem>
                                    <MenuItem value={'N'}>No</MenuItem>
                                </Select>
                            </FormControl> 
                        </div>

                        {/* Referencia */}
                        <div className="flex flex-col justify-between items-center max-w-[380px] flex-grow w-full">
                             <TextField id="referencia" label="Referencia" variant="outlined" value={localReferencia} disabled
                                sx={{
                                    '& .MuiOutlinedInput-input': {
                                    padding: '8px',
                                    },
                                    width: '100%',
                                    marginRight: '6px',
                                    marginBottom: '8px',
                                }}
                            />
                        </div>

                    </div>
                    <div className="flex flex-row justify-end items-center space-x-2">
                        <button className="mt-3 vp-btn-azul-sm" onClick={guardarCambiosLocal} ><SaveIcon /> Guardar Cambios</button>
                        <button className="mt-3 vp-btn-rojo-sm" onClick={cancelarCambios} ><SettingsBackupRestoreIcon /> Cancelar</button>
                    </div>
                </div>
            )}
        </>
    );
};
